/**
 * Host<->guest clock offset estimation for the calibrated latency probe.
 *
 * The offset is estimated from NTP-style round trips over the data channel:
 *   hostSend --> guestReceive --> guestSend --> hostReceive
 * Each completed round trip yields a candidate offset:
 *   offset = hostWallMsNow - guestWallMs - rtt/2
 * where rtt = hostWallMsNow - hostWallMsSent. The estimate is a best-quartile mean:
 * the lowest-RTT round trips are the closest to the true one-way path, and the
 * estimate averages the candidates within 25% of the best RTT. That is the standard
 * NTP trick for rejecting queueing noise: the fastest round trip dominates, and
 * jitter above the floor is ignored rather than averaged in.
 *
 * The estimate is deliberately conservative: it is only published once enough round
 * trips have landed, and a single slow round trip cannot move it (the quartile cut
 * keeps the best-RTT candidates, so jitter above the floor is ignored, not averaged
 * in).
 */

export const LATENCY_OFFSET_MIN_SAMPLES = 3;
const CALIBRATION_MAX_AGE_MS = 60_000;

type RoundTripSample = {
  offsetMs: number;
  rttMs: number;
  atMs: number;
};

export class LatencyOffsetTracker {
  private samples: RoundTripSample[] = [];
  private readonly minSamples: number;
  private readonly maxSamples: number;

  constructor(options: { minSamples?: number; maxSamples?: number } = {}) {
    this.minSamples = options.minSamples ?? LATENCY_OFFSET_MIN_SAMPLES;
    this.maxSamples = options.maxSamples ?? 30;
  }

  /** Host side: record a completed ping/pong round trip. */
  observeRoundTrip(
    _probeId: number,
    hostWallMsSent: number,
    hostWallMsNow: number,
    guestWallMs: number,
  ): void {
    const rttMs = hostWallMsNow - hostWallMsSent;
    if (!Number.isFinite(rttMs) || rttMs < 0) return;
    const offsetMs = hostWallMsNow - guestWallMs - rttMs / 2;
    if (!Number.isFinite(offsetMs)) return;
    this.expire(hostWallMsNow);
    const best = this.bestSamples()[0];
    if (best && rttMs <= best.rttMs * 1.25
      && Math.abs(offsetMs - best.offsetMs) > (rttMs + best.rttMs) / 2 + 2 + (hostWallMsNow - best.atMs) * 0.001) {
      // An equally precise exchange incompatible with the old clock invalidates its confidence.
      this.reset();
    }
    this.samples.push({ offsetMs, rttMs, atMs: hostWallMsNow });
    if (this.samples.length > this.maxSamples) this.samples.shift();
  }

  /**
   * Host->guest clock offset estimate (hostTime - guestTime), ms; null until trusted.
   *
   * Best-quartile mean: the lowest-RTT sample is the closest to the true one-way path, and
   * the estimate averages the candidates within 25% of that best RTT so a single good round
   * trip is not the whole answer but a queued/slow one above the quartile cannot drag the
   * estimate. Jitter above the floor is ignored, not averaged in.
   */
  offsetMs(): number | null {
    if (!this.isTrusted()) return null;
    const candidates = this.bestSamples();
    if (candidates.length === 0) return null;
    return candidates.reduce((sum, s) => sum + s.offsetMs, 0) / candidates.length;
  }

  /** True once enough round trips have landed for a stable estimate. */
  isTrusted(): boolean {
    return this.samples.length >= this.minSamples;
  }

  /** Conservative host-wall bounds, including path asymmetry and clock drift. */
  hostTimeBounds(guestWallMs: number, hostNowMs: number): { earliestMs: number; latestMs: number } | undefined {
    if (!Number.isFinite(guestWallMs) || !Number.isFinite(hostNowMs)) return undefined;
    this.expire(hostNowMs);
    const offsetMs = this.offsetMs();
    if (offsetMs === null) return undefined;
    // Use the same candidate center as calibrated latency, bounded by every selected
    // exchange's asymmetry and age. An averaged offset must not escape its own bounds.
    const uncertaintyMs = Math.max(...this.bestSamples().map(sample =>
      Math.abs(sample.offsetMs - offsetMs) + sample.rttMs / 2 + 1 + (hostNowMs - sample.atMs) * 0.001));
    return { earliestMs: guestWallMs + offsetMs - uncertaintyMs,
      latestMs: guestWallMs + offsetMs + uncertaintyMs };
  }

  reset(): void {
    this.samples = [];
  }

  private bestSamples(): RoundTripSample[] {
    const bestRtt = Math.min(...this.samples.map(sample => sample.rttMs));
    return this.samples.filter(sample => sample.rttMs <= bestRtt * 1.25);
  }

  private expire(hostNowMs: number): void {
    if (this.samples.some(sample => hostNowMs < sample.atMs)) this.reset();
    this.samples = this.samples.filter(sample => hostNowMs - sample.atMs <= CALIBRATION_MAX_AGE_MS);
  }
}
